import * as React from 'react'
import { ChevronLeft, ChevronRight } from 'react-feather'

import { Select } from 'main/ui'
import { formatTime } from 'main/util/time'
import { useTranslation } from 'react-i18next'
import { LOCIZE_NAMESPACE } from '../../../../../../common'
import { i18nKeyPrefix } from '../../../../i18n'
import { ScheduleTourModal_tourOpenings } from '../../../common/gql'
import DateCard from './DateCard'
import {
  ChevronContainer,
  chevronIconColor,
  chevronIconColorDisabled,
  chevronIconSize,
  Container,
  DayCarouselContainer,
  DaysContainer,
  SelectBoxContainer,
  SelectContainer,
  ValidationHint,
} from './Selector.styles'
import { dateCardLimit } from './util'

const PRE = `${i18nKeyPrefix}-SCHEDULE-MODAL-OPENING`

interface IProps {
  openings: ScheduleTourModal_tourOpenings[]
  selectedOpening: ScheduleTourModal_tourOpenings | null
  validationHint?: string
  onSelect: (selectedOpening: ScheduleTourModal_tourOpenings | null) => void
}

export default function Selector(props: IProps) {
  const { openings, selectedOpening, validationHint, onSelect } = props
  const { t } = useTranslation(LOCIZE_NAMESPACE)
  if (!openings.length) {
    return null
  }

  const dates = datesForOpenings(openings)

  // Currently selected date, determines which times to display in the select
  const [selectedDate, setSelectedDate] = React.useState<string | null>(
    selectedOpening && selectedOpening.date,
  )

  // Index of the first displayed date (relative to `dates` array)
  const [firstDisplayedDateIndex, setFirstDisplayedDateIndex] = React.useState<number>(
    initialFirstDisplayedDateIndex(dates, selectedOpening),
  )

  const canPageBackward = firstDisplayedDateIndex > 0
  const canPageForward = firstDisplayedDateIndex + dateCardLimit < dates.length

  return (
    <Container>
      <DayCarouselContainer>
        <ChevronContainer active={canPageBackward}>
          <ChevronLeft
            color={canPageBackward ? chevronIconColor : chevronIconColorDisabled}
            size={chevronIconSize}
            onClick={
              canPageBackward
                ? () => handlePageBackward(setFirstDisplayedDateIndex, onSelect)
                : undefined
            }
          />
        </ChevronContainer>

        <DaysContainer>
          {dates
            .slice(firstDisplayedDateIndex, firstDisplayedDateIndex + dateCardLimit)
            .map(date => (
              <DateCard
                key={date}
                date={date}
                active={date === selectedDate}
                onSelect={() => handleSelectDate(date, setSelectedDate, onSelect)}
              />
            ))}
        </DaysContainer>

        <ChevronContainer active={canPageForward}>
          <ChevronRight
            color={canPageForward ? chevronIconColor : chevronIconColorDisabled}
            size={chevronIconSize}
            onClick={
              canPageForward
                ? () => handlePageForward(dates, setFirstDisplayedDateIndex, onSelect)
                : undefined
            }
          />
        </ChevronContainer>
      </DayCarouselContainer>

      <SelectBoxContainer>
        <SelectContainer>
          <Select
            label={t(`${PRE}-TIME`)}
            value={selectedOpening && selectedOpening.id}
            disabled={!selectedDate}
            onChange={openingId => handleSelectOpening(openingId, openings, onSelect)}
          >
            <option value="" />
            {selectedDate &&
              openings
                .filter(opening => opening.date === selectedDate)
                .map(opening => (
                  <option key={opening.id} value={opening.id}>
                    {formatTime(opening.beginsAt)}
                  </option>
                ))}
          </Select>
        </SelectContainer>
      </SelectBoxContainer>

      {validationHint && <ValidationHint>{validationHint}</ValidationHint>}
    </Container>
  )
}

function handlePageBackward(
  setFirstDisplayedDateIndex: React.Dispatch<React.SetStateAction<number>>,
  setSelectedOpening: (selectedOpening: ScheduleTourModal_tourOpenings | null) => void,
) {
  setFirstDisplayedDateIndex(firstDisplayedDateIndex =>
    Math.max(firstDisplayedDateIndex - dateCardLimit, 0),
  )
  setSelectedOpening(null)
}

function handlePageForward(
  dates: string[],
  setFirstDisplayedDateIndex: React.Dispatch<React.SetStateAction<number>>,
  setSelectedOpening: (selectedOpening: ScheduleTourModal_tourOpenings | null) => void,
) {
  setFirstDisplayedDateIndex(firstDisplayedDateIndex =>
    Math.min(firstDisplayedDateIndex + dateCardLimit, dates.length - dateCardLimit),
  )
  setSelectedOpening(null)
}

function handleSelectDate(
  date: string,
  setSelectedDate: (date: string) => void,
  onSelect: (selectedOpening: ScheduleTourModal_tourOpenings | null) => void,
) {
  setSelectedDate(date)
  onSelect(null)
}

function handleSelectOpening(
  openingId: string | null,
  openings: ScheduleTourModal_tourOpenings[],
  onSelect: (opening: ScheduleTourModal_tourOpenings | null) => void,
) {
  const newSelectedOpening = openings.find(opening => opening.id === openingId) || null

  onSelect(newSelectedOpening)
}

function datesForOpenings(openings: ScheduleTourModal_tourOpenings[]) {
  const datesSet = openings.reduce((acc, opening) => acc.add(opening.date), new Set<string>())
  const dates = [...datesSet]

  return dates.sort()
}

function initialFirstDisplayedDateIndex(
  dates: string[],
  selectedOpening: ScheduleTourModal_tourOpenings | null,
) {
  if (!selectedOpening) {
    return 0
  }

  const dateIndex = dates.indexOf(selectedOpening.date)

  return dateIndex < dates.length - dateCardLimit ? dateIndex : dates.length - dateCardLimit
}
